import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Download, Search, X, List, CalendarDays } from "lucide-react";
import toast from "react-hot-toast";
import { exportDraftCalendarToExcel } from "./utils/exportDraftCalendar";
import { useMsal } from "@azure/msal-react";
import { trackPageView, trackUserAction } from "@/lib/analytics";
import "./DraftCalendarPage.css";
import { Statuses } from "@/utils/statuses";
import {
  useFiscalYears,
  useDraftCalendar,
  useDraftBusinesses,
  useDraftMeetingTypes,
  useAttendeeLevels,
  useLocations,
  useUpdateShowInGRC,
  useBulkUpdateStatus,
  useBulkUpdateStates,
  useUpdateSignOffAction,
  useBulkDraftPushToSummit,
  useCheckBusinessEligibility,
  useSubmitSignOffRequest,
  useTrackUserViewedComments,
  useCheckDraftBusiness,
  useCloseDraftCalendar,
  useCalendarSubmissionStatus,
  draftCalendarKeys
} from "@/services/draftCalendar";
import { useIsAdmin } from "@/hooks/useCurrentUser";
import { usePermissions, useCanAddMeeting, useCanEditMeeting, useCanPerformBulkAction, useCanViewToggleSettings } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useQueryClient } from "@tanstack/react-query";
import { DraftCalendarTable } from "./components/DraftCalendarTable";
import { MeetingFlyout } from "./components/MeetingFlyout";
import { BulkActionMenu } from "./components/BulkActionMenu";
import { ImportantDatesDialog } from "./components/ImportantDatesDialog";
import { BulkChangeStatusDialog, BULK_STATUS_CONSTANTS, type BulkChangeStatusValues } from "./components/BulkChangeStatusDialog";
import { BulkChangeSignOffStatusDialog, type BulkSignOffValues } from "./components/BulkChangeSignOffStatusDialog";
import { BulkPushToSummitResultDialog } from "./components/BulkPushToSummitResultDialog";
import { SubmitCalendarDialog } from "./components/SubmitCalendarDialog";
import { CloseCalendarDialog } from "./components/CloseCalendarDialog";
import { PushToSummitConfirmDialog } from "./components/PushToSummitConfirmDialog";
import type {
  DraftCalendarItem,
  DraftMeetingResponse,
  BulkActionInValidStatusResponse,
  BulkDraftPushToSummitRequest,
  BulkUpdateStatusRequest,
  RequestSignOffResponse,
  CheckUserBusinessMapResponse
} from "@/types/draftCalendar";

export function DraftCalendarPage() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const userIdEmail = currentUser?.email ?? "Unknown";

  // Track page view on mount
  useEffect(() => {
    trackPageView("Draft Calendar", userIdEmail);
  }, [userIdEmail]);

  const isAdmin = useIsAdmin();
  const { hasPermission } = usePermissions();
  const canAddMeeting = useCanAddMeeting();
  const canEditMeeting = useCanEditMeeting();
  const canBulkAction = useCanPerformBulkAction();
  const canToggleSettings = useCanViewToggleSettings();
  const canSubmit = hasPermission("CanSubmitToApprover");
  const isScheduler = currentUser?.roleName?.toLowerCase() === "scheduler";
  const userRole = currentUser?.roleName?.toLowerCase() ?? "";
  const userId = currentUser?.id ?? 0;

  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const { data: businessesData } = useDraftBusinesses();
  const businesses: Record<string, string[]> = businessesData ?? {};
  const { data: meetingTypesData } = useDraftMeetingTypes();
  const meetingTypesMap: Record<string, Record<string, string[]>> = (meetingTypesData as Record<string, Record<string, string[]>>) ?? {};
  const { data: attendeeLevelsData } = useAttendeeLevels();
  const attendeeLevels: string[] = attendeeLevelsData ?? [];
  const { data: locationsData } = useLocations();
  const locations: string[] = locationsData ?? [];

  const defaultFY = useMemo(
    () => fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0],
    [fiscalYears]
  );
  const [selectedFYId, setSelectedFYId] = useState<number>(0);
  const activeFYId = selectedFYId || defaultFY?.id || 0;
  const activeFY = fiscalYears.find((fy) => fy.id === activeFYId);

  const { data: draftItems = [], isPending, isFetching, refetch } =
    useDraftCalendar(activeFYId, userEmail);

  // Mirror Blazor GetSubmittedCalendarStatus — only for Admin/Scheduler (same as Blazor LoadDraftCalender)
  const { data: calendarStatus } = useCalendarSubmissionStatus(activeFYId);
  // isCalendarSubmitted = Blazor "isSubmitted" — true when calendar is Submitted or Closed
  const calendarStatusVal = calendarStatus?.calendarStatus ?? "";
  const isCalendarSubmitted = calendarStatusVal === "Submitted" || calendarStatusVal === "Closed";
  // IsCloseCalendarEnable = true only when status is exactly "Submitted" (not yet Closed)
  const isCloseCalendarEnabled = calendarStatusVal === "Submitted";

  // In TanStack Query v5, disabled queries return isPending=true permanently.
  // Only show the loading spinner when the query is actually fetching data.
  const isCalendarLoading = isPending && isFetching;

  const signoffReqSubmitted = draftItems.some((i) => i.signoffRequestSubmitted);
  const signOffProcessStarted = draftItems.some((i) => i.isSignOffProcessStarted);
  const responsibleForSignOff = draftItems.some((i) => i.canSubmitSignOff) || isAdmin || isScheduler;
  const isFYClosed = activeFY?.submissionStatus === "Closed";

  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [flyoutMeetingId, setFlyoutMeetingId] = useState<number | null>(null);
  const [flyoutViewOnly, setFlyoutViewOnly] = useState(true);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [copyFromData, setCopyFromData] = useState<DraftMeetingResponse | null>(null);

  const updateShowInGRC = useUpdateShowInGRC();
  const bulkUpdateStatus = useBulkUpdateStatus();
  const bulkUpdateStates = useBulkUpdateStates();
  const updateSignOffAction = useUpdateSignOffAction();
  const bulkDraftPushToSummit = useBulkDraftPushToSummit();
  const submitSignOffRequestMutation = useSubmitSignOffRequest();
  const trackUserViewedComments = useTrackUserViewedComments();
  const checkDraftBusiness = useCheckDraftBusiness();
  const closeDraftCalendar = useCloseDraftCalendar();

  // ── Submit Calendar state ──────────────────────────────────────────────────
  const [showSubmitCalendarDialog, setShowSubmitCalendarDialog] = useState(false);
  const [submitCalendarLoading, setSubmitCalendarLoading] = useState(false);
  const [submitCalendarResults, setSubmitCalendarResults] = useState<CheckUserBusinessMapResponse[]>([]);
  const [submitCalendarConfigured, setSubmitCalendarConfigured] = useState(false);
  const [submittingCalendar, setSubmittingCalendar] = useState(false);

  async function openSubmitCalendarDialog() {
    setShowSubmitCalendarDialog(true);
    setSubmitCalendarLoading(true);
    setSubmitCalendarResults([]);
    // Mirror Blazor GroupMeetingTypesByBusiness: group by Business + MeetingType
    const businesses = Array.from(
      new Map(
        draftItems
          .filter((i) => i.business && i.meetingType)
          .map((i) => [`${i.business}|${i.meetingType}`, { business: i.business!, meetingType: i.meetingType! }])
      ).values()
    );
    try {
      const results = await checkDraftBusiness.mutateAsync({ businesses });
      setSubmitCalendarResults(results);
      setSubmitCalendarConfigured(results.length > 0 && results.every((r) => r.isConfigured?.toLowerCase() === "configured"));
    } catch {
      toast.error("Failed to check business configurations. Please try again.");
      setShowSubmitCalendarDialog(false);
    } finally {
      setSubmitCalendarLoading(false);
    }
  }

  async function handleSubmitCalendarConfirm() {
    setSubmittingCalendar(true);
    try {
      // Mirror Blazor OnDraftCalendarSubmit:
      // Group draftItems by Business+MeetingType, then collect IDs for groups
      // whose business appears in the checked submitCalendarResults.
      const groupedDrafts = Object.values(
        draftItems
          .filter((i) => i.business && i.meetingType)
          .reduce<Record<string, typeof draftItems>>((acc, item) => {
            const key = `${item.business}|${item.meetingType}`;
            (acc[key] ??= []).push(item);
            return acc;
          }, {})
      );

      const idsToUpdate = groupedDrafts
        .filter((group) =>
          submitCalendarResults.some((r) => r.business === group[0].business)
        )
        .flatMap((group) => group.map((i) => i.id));

      if (idsToUpdate.length > 0) {
        const result = await bulkUpdateStates.mutateAsync({
          draftIds: idsToUpdate,
          state: Statuses.BusinessReview,
          fYId: activeFY?.id ?? 0,
        });

        if (result) {
          toast.success("Calendar submitted successfully.");
          setShowSubmitCalendarDialog(false);
          await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
        } else {
          toast.error("Update failed. Please try again.");
        }
      } else {
        toast.error("No eligible meetings found to submit.");
      }
    } catch {
      toast.error("Failed to submit calendar. Please try again.");
    } finally {
      setSubmittingCalendar(false);
    }
  }

  const [showCloseCalendarDialog, setShowCloseCalendarDialog] = useState(false);
  const [closingCalendar, setClosingCalendar] = useState(false);

  function handleCloseCalendar() {
    setShowCloseCalendarDialog(true);
  }

  async function handleCloseCalendarConfirm() {
    setClosingCalendar(true);
    try {
      const result = await closeDraftCalendar.mutateAsync({ fYID: activeFYId });
      if (result) {
        toast.success("Draft calendar closed successfully.");
        setShowCloseCalendarDialog(false);
        await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
      } else {
        toast.error("Failed to close draft calendar. Please try again later.");
      }
    } catch {
      toast.error("Failed to close draft calendar. Please try again later.");
    } finally {
      setClosingCalendar(false);
    }
  }

  const [showSignOffRequestDialog, setShowSignOffRequestDialog] = useState(false);
  const [signOffBusinesses, setSignOffBusinesses] = useState<RequestSignOffResponse[]>([]);
  const [signOffSelectAll, setSignOffSelectAll] = useState(true);
  const [isSignOffLoading, setIsSignOffLoading] = useState(false);
  const { refetch: fetchBusinessEligibility } =
    useCheckBusinessEligibility(activeFYId, false);

  async function openSignOffRequestDialog() {
    setIsSignOffLoading(true);
    setShowSignOffRequestDialog(true);
    const result = await fetchBusinessEligibility();
    // Mirror Blazor init: if isSignedOff, force isEligible=false; then
    // isChecked=true for eligible OR signed-off, false only when !isEligible && !isSignedOff
    const data: RequestSignOffResponse[] = (result.data ?? []).map((item) => {
      const effectiveEligible = item.isSignedOff ? false : item.isEligible;
      return {
        ...item,
        isEligible: effectiveEligible,
        isChecked: effectiveEligible || item.isSignedOff,
      };
    });
    const eligibleItems = data.filter((b) => b.isEligible);
    setSignOffBusinesses(data);
    setSignOffSelectAll(eligibleItems.length === 0 || eligibleItems.every((b) => b.isChecked));
    setIsSignOffLoading(false);
  }

  function toggleSignOffBusiness(business: string) {
    setSignOffBusinesses((prev) => {
      const next = prev.map((b) =>
        b.business === business && b.isEligible
          ? { ...b, isChecked: !b.isChecked }
          : b
      );
      const eligibleItems = next.filter((b) => b.isEligible);
      setSignOffSelectAll(eligibleItems.length === 0 || eligibleItems.every((b) => b.isChecked));
      return next;
    });
  }

  function toggleSignOffSelectAll() {
    const next = !signOffSelectAll;
    setSignOffSelectAll(next);
    setSignOffBusinesses((prev) =>
      prev.map((b) => b.isEligible ? { ...b, isChecked: next } : b)
    );
  }

  async function handleSubmitSignOffRequest() {
    const selectedBiz = signOffBusinesses.filter((b) => b.isChecked && b.isEligible).map((b) => b.business);
    if (selectedBiz.length === 0) {
      toast.error("Please select at least one eligible business to request sign-off.");
      return;
    }
    try {
      await submitSignOffRequestMutation.mutateAsync({ businesses: selectedBiz, fiscalYearId: activeFYId });
      toast.success("Sign-off request submitted successfully.");
      setShowSignOffRequestDialog(false);
      await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
    } catch {
      toast.error("Failed to submit sign-off request. Please try again later.");
    }
  }

  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showSignOffDialog, setShowSignOffDialog] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [pendingPushPayload, setPendingPushPayload] = useState<BulkDraftPushToSummitRequest | null>(null);
  const [showPushResult, setShowPushResult] = useState(false);
  const [pushTotalCount, setPushTotalCount] = useState(0);
  const [pushSuccessCount, setPushSuccessCount] = useState(0);
  const [failedMeetings, setFailedMeetings] = useState<BulkActionInValidStatusResponse[]>([]);

  function openCreate() { setCopyFromData(null); setFlyoutMeetingId(0); setFlyoutViewOnly(false); setLockedBy(null); }
  function openView(item: DraftCalendarItem) {
    setFlyoutMeetingId(item.id);
    setFlyoutViewOnly(true);
    setLockedBy(null);
    // Mirror Blazor OnRowClickHandler: if there are unseen comments, track that the user viewed them
    if (item.unSeenComments) {
      // Optimistically clear the badge in the cache immediately
      queryClient.setQueryData<DraftCalendarItem[]>(
        draftCalendarKeys.list(activeFYId, userEmail),
        (old) => old?.map((i) => i.id === item.id ? { ...i, unSeenComments: false } : i)
      );
      // Fire-and-forget the tracking call (mirrors Blazor's non-blocking await)
      void trackUserViewedComments.mutateAsync({ softHoldId: item.id, userId: userId });
    }
  }
  function handleFlyoutSaved() { setFlyoutMeetingId(null); setCopyFromData(null); void refetch(); }
  function handleFlyoutClose() { setFlyoutMeetingId(null); setCopyFromData(null); setCopyFromData(null); }

  function handleCopyToNew() {
    if (!flyoutMeetingId || flyoutMeetingId === 0) return;
    const data = queryClient.getQueryData<DraftMeetingResponse>(draftCalendarKeys.meeting(flyoutMeetingId));
    setCopyFromData(data ?? null);
    setFlyoutMeetingId(0);
    setFlyoutViewOnly(false);
    setLockedBy(null);
  }

  function handlePushToSummitSingle() {
    if (!flyoutMeetingId || flyoutMeetingId === 0) return;
    const singleId = flyoutMeetingId;
    setFlyoutMeetingId(null);
    setCopyFromData(null);
    setPendingPushPayload({ draftIds: [singleId], status: Statuses.Resolved, userId });
    setShowPushConfirm(true);
  }

  async function handleToggleGRC(id: number, showInGRC: boolean) {
    // Guard: mirrors Blazor OnToggleShowInGRCHandler – invalid ID check
    if (id <= 0) { toast.error("Cannot update GRC visibility: Invalid ID"); return; }
    // Guard: Removed / DU Declined meetings cannot be updated (mirrors Blazor BulkActionShowInGRC filter)
    const item = draftItems.find((i) => i.id === id);
    if (item?.state === "Removed" || item?.state === "DU Declined") {
      toast.error("Cannot update GRC visibility: meeting is in Removed or DU Declined state.");
      return;
    }
    try {
      const updatedIds = await updateShowInGRC.mutateAsync({ softHoldIds: [id], showInGRC });
      if (updatedIds.length > 0) {
        queryClient.setQueryData<DraftCalendarItem[]>(
          draftCalendarKeys.list(activeFYId, userEmail),
          (old) => old?.map((i) => updatedIds.includes(i.id) ? { ...i, showInGRC } : i)
        );
      } else {
        toast.error("Update failed. Please try again.");
      }
    } catch {
      toast.error("Update failed. Please try again.");
    }
  }

  async function handleBulkAction(actionId: string) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    if (selectedItems.length === 0) { toast.error("Select at least one meeting."); return; }
    try {
      if (actionId === "add-grc") {
        const eligibleIds = selectedItems
          .filter((item) => item.state !== BULK_STATUS_CONSTANTS.STATUS_REMOVED && item.state !== BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED)
          .map((item) => item.id);
        if (eligibleIds.length === 0) { toast.error("Selected meetings cannot be updated as they are in Removed or DU Declined state."); return; }
        const updatedIds = await updateShowInGRC.mutateAsync({ softHoldIds: eligibleIds, showInGRC: true });
        // Update cache for confirmed IDs only (green toggle), mirrors Blazor result.ForEach
        if (updatedIds.length > 0) {
          queryClient.setQueryData<DraftCalendarItem[]>(
            draftCalendarKeys.list(activeFYId, userEmail),
            (old) => old?.map((i) => updatedIds.includes(i.id) ? { ...i, showInGRC: true } : i)
          );
          toast.success("Added to GRC.");
        } else {
          toast.error("Update failed. Please try again.");
        }
      } else if (actionId === "remove-grc") {
        const eligibleIds = selectedItems
          .filter((item) => item.state !== BULK_STATUS_CONSTANTS.STATUS_REMOVED && item.state !== BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED)
          .map((item) => item.id);
        if (eligibleIds.length === 0) { toast.error("Selected meetings cannot be updated as they are in Removed or DU Declined state."); return; }
        const updatedIds = await updateShowInGRC.mutateAsync({ softHoldIds: eligibleIds, showInGRC: false });
        // Update cache for confirmed IDs only (grey toggle)
        if (updatedIds.length > 0) {
          queryClient.setQueryData<DraftCalendarItem[]>(
            draftCalendarKeys.list(activeFYId, userEmail),
            (old) => old?.map((i) => updatedIds.includes(i.id) ? { ...i, showInGRC: false } : i)
          );
          toast.success("Removed from GRC.");
        } else {
          toast.error("Update failed. Please try again.");
        }
      } else if (actionId === "change-status") {
        setShowStatusDialog(true); return;
      } else if (actionId === "change-signoff-status") {
        const eligibleMeetings = selectedItems.filter((item) => item.signOffStatus?.toLowerCase() !== "signed off");
        if (!eligibleMeetings.length) { toast.error("Selected meetings are not eligible for sign-off updates."); return; }
        setShowSignOffDialog(true); return;
      }
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
    } catch {
      toast.error("Bulk action failed. Please try again.");
    }
  }

  function getBulkStatusOptions() {
    const roleOptions: Record<string, string[]> = {
      admin:    [Statuses.BusinessApproved, Statuses.BusinessReview, Statuses.DUReview, Statuses.Scheduled, Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed],
      scheduler:[Statuses.BusinessApproved, Statuses.BusinessReview, Statuses.DUReview, Statuses.Scheduled, Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed],
      approver:       [Statuses.BusinessApproved, Statuses.DUReview, Statuses.Removed],
      businessreviewer:[Statuses.BusinessApproved, Statuses.DUReview, Statuses.Removed],
      viewonly:       [Statuses.NewRequest, Statuses.BusinessReview, Statuses.BusinessApproved, Statuses.DUReview, Statuses.Scheduled, Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed]
    };
    const roleScoped = roleOptions[userRole];
    if (roleScoped) return roleScoped;
    const existingStates = draftItems.map((item) => item.state).filter((s): s is string => !!s && s.trim().length > 0);
    const fallbackStates = [Statuses.BusinessApproved, Statuses.BusinessReview, Statuses.DUReview, Statuses.Scheduled, Statuses.Resolved, BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED, BULK_STATUS_CONSTANTS.STATUS_REMOVED];
    return Array.from(new Set([...existingStates, ...fallbackStates]));
  }

  async function handleStatusDialogSubmit(values: BulkChangeStatusValues) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    const selectedIds = selectedItems.map((item) => item.id);
    const isApproverScopedRole = userRole === "approver" || userRole === "businessreviewer";
    const approverEligibleCurrentStatusesByTarget: Record<string, string[]> = {
      "Business Approved": ["New Request", "Business Review"],
      "DU Review": ["New Request", "Business Review"],
      Removed: ["New Request", "Business Review"]
    };
    let statusEligibleItems = selectedItems;
    if (isApproverScopedRole) {
      const allowedCurrentStates = approverEligibleCurrentStatusesByTarget[values.status] ?? [];
      if (allowedCurrentStates.length > 0) {
        const allowedSet = new Set(allowedCurrentStates.map((s) => s.toLowerCase()));
        statusEligibleItems = selectedItems.filter((item) => allowedSet.has((item.state ?? "").trim().toLowerCase()));
      }
    }
    const draftIds = statusEligibleItems.filter((item) => item.state !== values.status).map((item) => item.id);
    if (!draftIds.length) { toast.error("No Meeting(s) are there to update please try again."); return; }
    if (values.status === BULK_STATUS_CONSTANTS.STATUS_RESOLVED) {
      setPendingPushPayload({ draftIds: selectedIds, status: values.status, comment: values.comment || "", userId });
      setShowStatusDialog(false); setShowPushConfirm(true); return;
    }
    const payload: BulkUpdateStatusRequest = { draftIds, status: values.status, comment: values.comment || "", duDeclineReason: values.duDeclineReason || "", duDeclineComments: values.duDeclineComments || "", userId };
    const updatedCount = await bulkUpdateStatus.mutateAsync(payload);
    if (updatedCount <= 0) { toast.error("No Meeting(s) are there to update please try again."); return; }
    const skipped = selectedItems.length - draftIds.length;
    const skippedPrefix = skipped > 0 ? (values.status === BULK_STATUS_CONSTANTS.STATUS_REMOVED ? `${skipped} items have been skipped. ` : `${skipped} Meeting(s) could not be updated due to their current status. `) : "";
    toast.success(`${skippedPrefix}${updatedCount} Meeting(s) has been updated successfully.`);
    setShowStatusDialog(false); setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  async function handleSignOffDialogSubmit(values: BulkSignOffValues) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    let eligible = selectedItems.filter((item) => item.signOffStatus?.toLowerCase() !== "signed off");
    if (userRole === "approver" || userRole === "businessreviewer") eligible = eligible.filter((item) => item.canSubmitSignOff);
    if (!eligible.length) { toast.error("No eligible meetings selected. Only valid statuses can be updated for SignOff."); return; }
    const result = await updateSignOffAction.mutateAsync({ draftIds: eligible.map((item) => item.id), signOffAction: values.signOffAction });
    if (result <= 0) { toast.error("No meetings were updated. Please try again."); return; }
    const skipped = selectedItems.length - eligible.length;
    let message = `${result} meeting(s) have been signed off successfully.`;
    if (skipped > 0) message += ` ${skipped} meeting(s) were skipped as they are not in a valid status for sign-off.`;
    toast.success(message);
    setShowSignOffDialog(false); setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  async function handlePushToSummitConfirm() {
    if (!pendingPushPayload) return;
    const result = await bulkDraftPushToSummit.mutateAsync(pendingPushPayload);
    setShowPushConfirm(false);
    setPushTotalCount(pendingPushPayload.draftIds.length);
    setPushSuccessCount(result.successCount || 0);
    setFailedMeetings(result.bulkActionInValidStatusResponses || []);
    setShowPushResult(true); setPendingPushPayload(null); setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  const bulkActions = useMemo(() => {
    const actions = [];
    if (canToggleSettings) {
      actions.push({ id: "add-grc", label: "Add to GRC" });
      actions.push({ id: "remove-grc", label: "Remove from GRC" });
    }
    actions.push({
      id: "change-status",
      label: "Change Status",
      // Mirrors Blazor: Enabled="!IsSignoffReqSubmitted || IsAdmin || IsScheduler"
      //disabled: signoffReqSubmitted && !isAdmin && !isScheduler,
      title: signoffReqSubmitted && !isAdmin && !isScheduler
        ? "Sign-off in progress – only Admins and Schedulers can change status"
        : undefined
    });
    // if (responsibleForSignOff || isAdmin || isScheduler) {
    //   actions.push({ id: "change-signoff-status", label: "Change SignOff Status" });
    // }
    actions.push({
      id: "change-signoff-status",
      label: "Change SignOff Status",
      //disabled: signoffReqSubmitted && !isAdmin && !isScheduler,
    });
    return actions;
  }, [canToggleSettings, signoffReqSubmitted, isAdmin, isScheduler, responsibleForSignOff]);

  const addMeetingLabel = isAdmin || isScheduler || !signOffProcessStarted
    ? "Add Meeting"
    : signoffReqSubmitted && signOffProcessStarted ? "Request Meeting" : "Add Meeting";

  const filteredItems = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? draftItems.filter((i) => i.meetingName?.toLowerCase().includes(q) || i.business?.toLowerCase().includes(q) || i.meetingId?.toLowerCase().includes(q))
      : draftItems;
  }, [draftItems, searchTerm]);

  const filteredCount = filteredItems.length;

  return (
    <main className="draft-calendar-scroll-page flex flex-col" aria-label="Draft Calendar">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        {/* Search */}
        <div className="relative">
          <label htmlFor="dc-search" className="sr-only">Search meetings</label>
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input id="dc-search" type="search" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search meetings"
            className="h-8 w-64 border border-slate-300 pl-8 pr-8 text-sm focus:outline-none focus:border-slate-500" />
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm("")} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Export */}
          {viewMode === "list" && (
            <button
              type="button"
              aria-label="Export to Excel"
              className="flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-100"
              title="Export list to Excel"
              onClick={() => exportDraftCalendarToExcel(filteredItems)}
            >
              <Download className="h-4 w-4" />
            </button>
          )}

          {/* List / Calendar view toggle */}
          <div className="flex overflow-hidden border border-slate-300">
            <button type="button" onClick={() => setViewMode("list")} aria-label="List view"
              aria-pressed={viewMode === "list"}
              className={`flex h-8 w-8 items-center justify-center ${viewMode === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <List className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => navigate("/calendarscheduleview")}
              aria-label="Calendar view" aria-pressed={false}
              className="flex h-8 w-8 items-center justify-center border-l border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>

          {/* Bulk Actions */}
          {canBulkAction && (
            <BulkActionMenu actions={bulkActions} onAction={handleBulkAction} disabled={selected.size === 0} />
          )}

          {/* Fiscal year selector */}
          <div className="flex items-center gap-1.5">
            <label htmlFor="fy-select" className="sr-only">Fiscal Year</label>
            {loadingFY ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            ) : (
              <select id="fy-select" value={activeFYId}
                onChange={(e) => {
                    const newFYId = Number(e.target.value);
                    // Remove cached data for the new FY so stale data from a previous
                    // load of that FY is never shown — forces a fresh fetch + loading spinner
                    queryClient.removeQueries({ queryKey: draftCalendarKeys.list(newFYId, userEmail) });
                    setSelectedFYId(newFYId);
                    setSelected(new Set());
                  }}
                className="FY-dropdown">
                {fiscalYears.map((fy) => (
                  <option key={fy.id} value={fy.id}>
                    {fy.code}{fy.submissionStatus === "Closed" ? " (Closed)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Add Meeting — show when user has permission OR while permissions are still loading */}
          {(!currentUser || canAddMeeting) && !isFYClosed && (
            <button type="button" onClick={openCreate}
              className="Add-Request-button border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
              {addMeetingLabel}
            </button>
          )}

          {/* Calendar Options */}
          {canToggleSettings && (
            <CalendarOptionsMenu canSubmit={canSubmit} signoffReqSubmitted={signoffReqSubmitted}
              signOffProcessStarted={signOffProcessStarted} isFYClosed={isFYClosed}
              isCalendarSubmitted={isCalendarSubmitted}
              isCloseCalendarEnabled={isCloseCalendarEnabled}
              onRequestSignOff={() => void openSignOffRequestDialog()}
              onSubmitCalendar={() => void openSubmitCalendarDialog()}
              onCloseCalendar={() => void handleCloseCalendar()} />
          )}
        </div>
      </div>

      {/* ── Status bar ── */}
      <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
        <span>
          {selected.size > 0 ? `${selected.size} selected of ${filteredCount}` : `${filteredCount} meeting${filteredCount !== 1 ? "s" : ""}`}
        </span>
        {isFetching && !isPending && (
          <span className="flex items-center gap-1 text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Refreshing&hellip;
          </span>
        )}
        {isFYClosed && <span className="rounded bg-red-100 px-2 py-0.5 text-red-700 font-medium">Fiscal Year Closed</span>}
      </div>

      {/* ── Content ── */}
      <div className="min-h-0 flex-1 overflow-hidden px-0">
        {isCalendarLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading calendar&hellip;</span>
          </div>
        ) : viewMode === "calendar" ? (
          <div className="flex items-center justify-center py-20 text-sm text-slate-500">Calendar view coming soon.</div>
        ) : (
          <DraftCalendarTable items={draftItems} searchTerm={searchTerm} selected={selected}
            onSelectionChange={setSelected} onRowClick={openView} canEdit={canEditMeeting}
            canBulkAction={canBulkAction} canToggleSettings={canToggleSettings}
            signOffProcessStarted={signOffProcessStarted} isAdmin={isAdmin} isScheduler={isScheduler}
            onToggleGRC={canToggleSettings ? handleToggleGRC : undefined} />
        )}
      </div>

      {/* ── Footer bar ── */}
      <div className="flex items-center justify-between border-t border-slate-100 bg-white px-4 py-1.5">
        <div className="flex items-center gap-4">
          <a href="#" onClick={(e) => { e.preventDefault(); setShowHolidayDialog(true); }}
            className="holiday-link">
            View Important Dates &amp; Holidays
          </a>
          {/* Core/Secondary legend — mirrors Blazor: hidden for Approver/BusinessReviewer roles */}
          {userRole !== "approver" && userRole !== "businessreviewer" && (
            <div className="flex items-center gap-3 text-xs">
              <span className="event-status-draft-legend-Core" />
              <span className="event-status-legend">Core</span>
              <span className="event-status-draft-legend-Secondary" />
              <span className="event-status-legend">Secondary</span>
            </div>
          )}
        </div>
        <span className="total-count">Total Count: {filteredCount}</span>
      </div>

      <BulkChangeStatusDialog open={showStatusDialog} statuses={getBulkStatusOptions()}
        onClose={() => setShowStatusDialog(false)} onSubmit={handleStatusDialogSubmit}
        submitting={bulkUpdateStatus.isPending || bulkDraftPushToSummit.isPending} />

      <BulkChangeSignOffStatusDialog open={showSignOffDialog} onClose={() => setShowSignOffDialog(false)}
        onSubmit={handleSignOffDialogSubmit} submitting={updateSignOffAction.isPending} />

      {showPushConfirm && (
        <PushToSummitConfirmDialog
          open={showPushConfirm}
          message="Are you sure you want to push selected meetings to Summit?"
          submitting={bulkDraftPushToSummit.isPending}
          onCancel={() => setShowPushConfirm(false)}
          onConfirm={() => void handlePushToSummitConfirm()}
        />
      )}

      <BulkPushToSummitResultDialog open={showPushResult} onClose={() => setShowPushResult(false)}
        totalCount={pushTotalCount} successCount={pushSuccessCount} failedMeetings={failedMeetings} />

      {showSignOffRequestDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={() => setShowSignOffRequestDialog(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setShowSignOffRequestDialog(false); }}
          tabIndex={-1}>
          <div role="dialog" aria-modal="true" aria-labelledby="signoff-request-title"
            className="w-full max-w-[720px] border border-slate-300 bg-white shadow-xl"
            style={{ maxHeight: "min(80vh, 600px)", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h2 id="signoff-request-title" className="text-base font-semibold text-black">Request Sign Off</h2>
              <button type="button" onClick={() => setShowSignOffRequestDialog(false)} aria-label="Close"
                className="text-slate-500 hover:text-slate-900">&#x2715;</button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <p className="mb-4 text-sm leading-5 text-black">
                This will send all meetings to the business for their sign off. You must have one of the
                following statuses for each meeting in order to request sign off:{" "}
                <strong>Scheduled</strong>, <strong>DU Declined</strong>, or <strong>Remove</strong>.
              </p>
              <div className="mb-2 flex items-center gap-2 font-semibold" style={{ color: "#63666A" }}>
                Business
                <button type="button" onClick={toggleSignOffSelectAll} className="font-normal" style={{ color: "#007CB0" }}>
                  {signOffSelectAll ? "Deselect All" : "Select All"}
                </button>
              </div>
              {isSignOffLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading businesses&hellip;
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {signOffBusinesses.map((item) => {
                    // item.isEligible is already post-processed (false when signed-off)
                    // indeterminate = signed off but not eligible (shown as half-checked, disabled)
                    const isIndeterminate = !item.isEligible && item.isSignedOff;
                    const labelStyle = (item.isEligible && !item.isSignedOff)
                      ? { color: "black", fontStyle: "normal" as const }
                      : (!item.isEligible && item.isSignedOff)
                      ? { color: "black", fontStyle: "normal" as const }
                      : { color: "#DA291C", fontStyle: "italic" as const, opacity: 0.6 };
                    return (
                      <div key={item.business} className="flex items-center gap-2">
                        <input type="checkbox" id={`signoff-biz-${item.business}`}
                          checked={item.isChecked} disabled={!item.isEligible}
                          ref={(el) => { if (el) el.indeterminate = isIndeterminate; }}
                          onChange={() => toggleSignOffBusiness(item.business)}
                          className="h-4 w-4 accent-black disabled:opacity-50" />
                        <label htmlFor={`signoff-biz-${item.business}`} className="text-sm" style={labelStyle}>
                          {item.business}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-slate-200 px-4 py-3">
              <div className="flex justify-center gap-3">
                <button type="button" onClick={() => setShowSignOffRequestDialog(false)}
                  className="cancel-button-window">
                  Cancel
                </button>
                <button type="button" onClick={() => void handleSubmitSignOffRequest()}
                  disabled={submitSignOffRequestMutation.isPending}
                  className="submit-button-sign-off disabled:opacity-50">
                  {submitSignOffRequestMutation.isPending ? "Submitting\u2026" : "Request Sign Off"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {flyoutMeetingId !== null && (
        <MeetingFlyout meetingId={flyoutMeetingId} fiscalYear={activeFY} businesses={businesses}
          meetingTypes={meetingTypesMap} attendeeLevels={attendeeLevels} locations={locations}
          isViewOnly={flyoutViewOnly} lockedBy={lockedBy}
          canEdit={canEditMeeting && draftItems.find((i) => i.id === flyoutMeetingId)?.state !== Statuses.CreatedInSummit}
          canCopyToNew={canEditMeeting}
          isBusinessApprover={userRole === "approver"}
          signOffStatus={draftItems.find((i) => i.id === flyoutMeetingId)?.signOffStatus ?? null}
          copyFromData={copyFromData ?? undefined}
          canPushToSummit={isAdmin || isScheduler}
          onEdit={() => setFlyoutViewOnly(false)} onSaved={handleFlyoutSaved} onClose={handleFlyoutClose}
          onCopyToNew={handleCopyToNew}
          onPushToSummit={handlePushToSummitSingle} />
      )}

      <ImportantDatesDialog open={showHolidayDialog} onClose={() => setShowHolidayDialog(false)} />

      <SubmitCalendarDialog
        open={showSubmitCalendarDialog}
        loading={submitCalendarLoading}
        results={submitCalendarResults}
        isConfigured={submitCalendarConfigured}
        submitting={submittingCalendar}
        onConfirm={() => void handleSubmitCalendarConfirm()}
        onClose={() => setShowSubmitCalendarDialog(false)}
      />

      <CloseCalendarDialog
        open={showCloseCalendarDialog}
        closing={closingCalendar}
        onConfirm={() => void handleCloseCalendarConfirm()}
        onClose={() => setShowCloseCalendarDialog(false)}
      />
    </main>
  );
}

interface CalendarOptionsProps {
  canSubmit: boolean;
  signoffReqSubmitted: boolean;
  signOffProcessStarted: boolean;
  isFYClosed: boolean;
  /** Blazor: isEnabled = !isSubmitted (calendar not yet submitted) */
  isCalendarSubmitted: boolean;
  /** Blazor: IsCloseCalendarEnable = calendarStatus === "Submitted" */
  isCloseCalendarEnabled: boolean;
  onRequestSignOff: () => void;
  onSubmitCalendar: () => void;
  onCloseCalendar: () => void;
}

function CalendarOptionsMenu({ canSubmit, signoffReqSubmitted, signOffProcessStarted, isFYClosed, isCalendarSubmitted, isCloseCalendarEnabled, onRequestSignOff, onSubmitCalendar, onCloseCalendar }: CalendarOptionsProps) {
  const [open, setOpen] = useState(false);
  // Mirrors Blazor:
  // Submit Calendar:  CanSubmitToApprover && isEnabled        (isEnabled = !isSubmitted)
  // Request Sign Off: CanSubmitToApprover && !isEnabled
  // Close Calendar:   CanSubmitToApprover && !isEnabled && IsSignoffReqSubmitted && IsCloseCalendarEnable
  const canSubmitCalendar = canSubmit && !isCalendarSubmitted;
  const canRequestSignOff = canSubmit && isCalendarSubmitted;
  const canCloseCalendar  = canSubmit && isCalendarSubmitted && signoffReqSubmitted && isCloseCalendarEnabled;

  return (
    <div className="relative">
      <button type="button" aria-haspopup="true" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="submit-button inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium">
        Calendar Options &#x25BE;
      </button>
      {open && (
        <ul role="menu" className="bulkAction-custom-popup absolute right-0 top-full z-50 mt-1"
          onMouseLeave={() => setOpen(false)}>
          <li role="none">
            <button role="menuitem" type="button" disabled={!canSubmitCalendar}
              onClick={() => { setOpen(false); onSubmitCalendar(); }}
              className="drop-down-buttons w-full px-4 py-2 text-left text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Submit Calendar
            </button>
          </li>
          <li role="none">
            <button role="menuitem" type="button" disabled={!canRequestSignOff}
              onClick={() => { setOpen(false); void onRequestSignOff(); }}
              className="drop-down-buttons w-full px-4 py-2 text-left text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Request Sign Off
            </button>
          </li>
          <li role="none">
            <button role="menuitem" type="button" disabled={!canCloseCalendar}
              onClick={() => { setOpen(false); void onCloseCalendar(); }}
              className="drop-down-buttons w-full px-4 py-2 text-left text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Close Calendar
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
